import { gql, useMutation } from "@apollo/client";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Button, Form, Input, message, Modal } from "antd";
import FormItem from "antd/es/form/FormItem";
import TextArea from "antd/lib/input/TextArea";
import CIQDatePicker from "components/custom-date-picker";
import { ErrorMessages, SidepanelStyles } from "constants/index";
import { ProjectContext } from "context/ProjectProvider";
import { MUTATION_CREATE_FEATURE_INSTANCE } from "entity-app/graphQL/ciq-feature-mutations";
import { useAutoFocusFirstField } from "hooks/useAutoFocusFirstField";
import { useContext, useRef, useState } from "react";
import { validatorForFieldValue } from "utils/utils";
import GCReviewersDropdown from "components/gc-reveiewer-dropdown";
import {
  DATE_FORMAT_MMDDYYYY,
  DATE_FORMAT_YYYYMMDD,
  DateUtils
} from "utils/dateutils";

function CreateDesignPackageComponent(props: {
  setDrawerOpen: (open: boolean) => void;
  showDrawerOpen: boolean;
  modelTitle: string;
  onDesignPackageCreated: any;
}) {
  const [form] = Form.useForm();
  const { setDrawerOpen, showDrawerOpen, modelTitle, onDesignPackageCreated } =
    props;
  const [isLoading, setLoading] = useState(false);
  const { gqlClientForProject, tokenRetrievalState } =
    useContext(ProjectContext);
  const token = tokenRetrievalState?.token;
  const formContainerRef = useRef<HTMLDivElement | null>(null);
  useAutoFocusFirstField(formContainerRef, {
    enabled: showDrawerOpen,
    modal: true
  });

  const [createDesignPackage] = useMutation<any>(
    gql(MUTATION_CREATE_FEATURE_INSTANCE),

    {
      client: gqlClientForProject
    }
  );

  const onFinish = async (values: any) => {
    const instancePayload: any = {
      number: values?.number,
      title: values?.title,
      description: values?.description,
      gc_representative: values?.gc_representative,
      start_date: values?.start_date?.format(DATE_FORMAT_YYYYMMDD)
    };

    const variables = {
      feature_type: "DESIGN_PACKAGE",
      feature_instances: [instancePayload]
    };

    setLoading(true);

    const creatResponse: any = await createDesignPackage({
      variables
    });

    setLoading(false);

    if (creatResponse?.data?.insert_feature_instances_multi?.ids?.length) {
      onDesignPackageCreated(values?.title);
    } else {
      message.error("Unable to create Design Package.");
    }
  };

  return (
    <Modal
      className="custom-drawer with-body-scroll"
      title={modelTitle}
      width={420}
      style={SidepanelStyles.style}
      footer={
        <div
          className="flex justify-end space-x-2 items-center"
          data-testid="index-modal"
        >
          <Button
            form="CreateDesignPackageForm"
            disabled={isLoading}
            onClick={() => {
              setDrawerOpen(false);
            }}
            data-testid="index-button-cancel"
          >
            Cancel
          </Button>
          <Button
            form="CreateDesignPackageForm"
            loading={isLoading}
            disabled={isLoading}
            htmlType="submit"
            type="primary"
            data-testid="index-button-create-design-package"
          >
            Create
          </Button>
        </div>
      }
      open={showDrawerOpen}
      onCancel={() => {
        setDrawerOpen(false);
      }}
      destroyOnHidden
      data-testid="index-modal-create-design-package"
    >
      <div ref={formContainerRef}>
        <Form
          preserve={false}
          form={form}
          id="CreateDesignPackageForm"
          scrollToFirstError
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            start_date: DateUtils.dateTimeObj()
          }}
        >
          <FormItem
            name="number"
            label="Design Package Number"
            rules={[
              {
                required: true,
                validateTrigger: "onSubmit",
                message: ErrorMessages.designPackgeNumber
              },
              {
                validator: (_, value) => {
                  return validatorForFieldValue(
                    "number",
                    value?.trim(),
                    "DESIGN_PACKAGE",
                    token,
                    ErrorMessages.DesignPackageNumberMsg
                  );
                }
              }
            ]}
          >
            <Input
              data-autofocus="true"
              placeholder="Enter Design Package Number"
            />
          </FormItem>
          <FormItem
            name="title"
            label="Design Package Title"
            rules={[
              {
                required: true,
                validateTrigger: "onSubmit",
                message: ErrorMessages.designPackgeTitle
              },
              {
                validator: (_, value) => {
                  return validatorForFieldValue(
                    "title",
                    value?.trim(),
                    "DESIGN_PACKAGE",
                    token,
                    ErrorMessages.DesignPackageTitleMsg
                  );
                }
              }
            ]}
          >
            <Input placeholder="Enter Design Package Title" />
          </FormItem>

          <FormItem name="description" label="Description">
            <TextArea rows={2} placeholder="Enter a Description" />
          </FormItem>

          <Form.Item label="GC Representative" name="gc_representative">
            <GCReviewersDropdown placeholder="Select GC Representative" />
          </Form.Item>

          <FormItem
            name="start_date"
            label="Schematic Design"
            tooltip={{
              title:
                "All milestones will have default dates 20 days apart. Change them later to your committed dates.",
              icon: <InfoCircleOutlined data-testid="index-start-date-info" />
            }}
            rules={[
              {
                required: true,
                message: "Enter Schematic Design date."
              }
            ]}
            getValueFromEvent={(date) =>
              date ?? form.getFieldValue("start_date")
            }
          >
            <CIQDatePicker
              className="w-60"
              format={DATE_FORMAT_MMDDYYYY}
              allowClear={false}
              placeholder="Select date"
              data-testid="index-datepicker-start-date"
            />
          </FormItem>
        </Form>
      </div>
    </Modal>
  );
}

export default CreateDesignPackageComponent;
